import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { trackMapEmpty, trackMapError, trackMapRGExpand, trackMapStateEngage, trackMapTabChange, trackMapView } from '../lib/analytics'
import type { ApprovalMapData, BookIndex, PlaceBooks, Status, WorldData } from './availability'
import { booksInCountry, booksInState, booksInUnitedStates, countryQualifiers, indexBooks, isDfsOnlyCountry, isJurisdiction, statusOf, stateStatus, unitedStatesStatus } from './availability'
import BookTile from './BookTile'
import ListView from './ListView'
import type { MapShape } from './MapSurface'
import MapSurface from './MapSurface'
import { US_MAP_VIEWBOX, US_STATES } from './usMapPaths'

// Standalone availability-map widget for cross-origin embed via the
// ApprovalMapWidget.init() SDK. Three tabs over one payload:
//
//   USA    — 50 states + DC, coloured by the sport's curated approvals
//            (betting layer) and its DFS carriers' live states (DFS layer).
//   GLOBAL — 175 countries, coloured by CARRIER reach. Country colour cannot
//            come from sport-level approvals the way state colour does:
//            `sport_international` holds 3 distinct values catalog-wide, so
//            that map would render empty. See api/index.ts for the full note.
//   EUROPE — the same world geometry clipped to Europe (Atlantic to the Urals,
//            no Greenland or Iceland), so the region fills the frame. Identical
//            colouring to Global; it is a zoom, not a different dataset.
//            Its extent happens to land at the USA map's aspect ratio, so those
//            two tabs render the same height.
//   LIST   — every available place as text, region-grouped, searchable.
//
// Every map is width:100% with its natural height, so the tabs differ in height
// (world 2.36:1, USA 1.63:1, Europe 1.63:1). Deliberate — Cole's call after the
// alternative (padding every map to one aspect) left large dead margins.
//
// The opening tab is geo-targeted: US visitors land on USA, everyone else on
// GLOBAL. Hovering (or tapping) a shape opens a popover of the sportsbooks and
// DFS apps live there as brand tiles, clickable when an affiliate URL exists.

export type { ApprovalMapData } from './availability'

export interface ApprovalMapLabels {
  /** USA + world maps. */
  betting: string
  /** USA map: the betting + DFS bucket, when some of it also has prediction. */
  bettingDfsPrediction: string
  /** USA map: the betting + DFS bucket, when none of it has prediction. */
  bettingAndDfs: string
  /** USA map: DFS, with or without a prediction market. */
  dfsOnly: string
  /** World maps: betting + prediction. */
  bettingAndPrediction: string
  /** Both families: a prediction market is the only thing live. */
  prediction: string
  none: string
  approvedNoBooks: string
  sportsbooksHeading: string
  dfsHeading: string
  predictionHeading: string
  tabUsa: string
  tabGlobal: string
  tabEurope: string
  tabList: string
}

// Legend keys name what IS available and nothing else — "Available" is implied
// by the key being on a map key at all, and dropping it keeps the row short
// enough to sit on one line on a phone (Cole's call). "No Availability" keeps
// its wording: it is the one row that is not naming a product.
const DEFAULT_LABELS: ApprovalMapLabels = {
  betting: 'Betting',
  bettingDfsPrediction: 'Betting, DFS and Prediction',
  bettingAndDfs: 'Betting and DFS',
  dfsOnly: 'DFS',
  bettingAndPrediction: 'Betting and Prediction',
  prediction: 'Prediction',
  none: 'No Availability',
  approvedNoBooks: 'Betting approved — no sportsbooks live here yet',
  sportsbooksHeading: 'Sportsbooks',
  dfsHeading: 'DFS',
  predictionHeading: 'Prediction Markets',
  tabUsa: 'USA',
  tabGlobal: 'Global',
  tabEurope: 'Europe',
  tabList: 'List View',
}

// Sportsbook mode (`sportsbook:` init option) — every "book" in the payload is
// a SPORT the operator carries and the layers are the operator's own reach, so
// the copy flips: the legend names the operator's product, the popover heading
// names what it lists. Partner `labels` still win over these.
const SPORTSBOOK_LABELS: Partial<ApprovalMapLabels> = {
  betting: 'Sportsbook live',
  dfsOnly: 'DFS live',
  prediction: 'Prediction market live',
  approvedNoBooks: 'Live here — no carried sport approved yet',
  sportsbooksHeading: 'Sports available',
  dfsHeading: 'Sports available',
  predictionHeading: 'Sports available',
}

// Fallback layer palette for sports without brand identity colors set.
const FALLBACK = { betting: '#0DB5FF', dfs: '#64748B', both: '#0A2342' }

export type MapTab = 'usa' | 'global' | 'europe' | 'list'

/** The map tabs, in the order the right arrow cycles them. */
const MAP_TABS = ['usa', 'global', 'europe'] as const
type MapOnlyTab = typeof MAP_TABS[number]

interface Props {
  /** Sport slug (or 'general'). One of sport / sportsbook is required. */
  sport?: string
  /** Sportsbook slug — the operator's own availability map. */
  sportsbook?: string
  apiBase: string
  theme: 'light' | 'dark'
  labels?: Partial<ApprovalMapLabels>
  /** Overrides the geo-targeted opening tab. Ignored without `world`. */
  defaultTab?: MapTab
  /** Sub-affiliate embed flag — forwarded as ?aff= on the payload fetch. */
  subAffiliate?: string
  /**
   * The world dataset — switches on the tabs, arrows and geo-targeted opening
   * tab. Injected by the entry (sdk.ts) rather than imported here so a
   * hypothetical USA-only build could still tree-shake the ~110 KB of
   * geometry; absent, the widget renders the USA map alone.
   */
  world?: WorldData
  onEmpty: () => void
}

function parseHex(hex: string): [number, number, number] | null {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim())
  if (!m?.[1]) return null
  let h = m[1]
  if (h.length === 3) h = h.split('').map(c => c + c).join('')
  return [Number.parseInt(h.slice(0, 2), 16), Number.parseInt(h.slice(2, 4), 16), Number.parseInt(h.slice(4, 6), 16)]
}

function mixHex(a: string, b: string, t: number): string {
  const ca = parseHex(a)
  const cb = parseHex(b)
  if (!ca || !cb) return a
  const c = ca.map((v, i) => Math.round(v + ((cb[i] ?? v) - v) * t))
  return `#${c.map(v => v.toString(16).padStart(2, '0')).join('')}`
}

// Hover tint: lighten dark fills toward white, darken very light fills so
// the hover state is visible either way (the old WP maps hand-tuned these;
// we derive them).
function hoverColor(hex: string): string {
  const c = parseHex(hex)
  if (!c) return hex
  const luminance = (0.299 * c[0] + 0.587 * c[1] + 0.114 * c[2]) / 255
  return luminance > 0.72 ? mixHex(hex, '#000000', 0.15) : mixHex(hex, '#ffffff', 0.25)
}

const US_VB = US_MAP_VIEWBOX.split(' ').map(Number) as [number, number, number, number]
const US_SHAPES: MapShape[] = US_STATES.map(s => ({ id: s.id, name: s.name, d: s.d }))

/** Geo lookup for the opening tab. Never allowed to delay first paint. */
const GEO_TIMEOUT_MS = 1500

async function resolveHomeTab(apiBase: string): Promise<MapTab> {
  try {
    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), GEO_TIMEOUT_MS)
    const res = await fetch(`${apiBase}/api/v1/geo`, { signal: ctrl.signal })
    clearTimeout(timer)
    if (!res.ok) return 'global'
    const geo = await res.json() as { countryCode?: string | null }
    return geo.countryCode === 'US' ? 'usa' : 'global'
  }
  catch {
    // Ad blockers, anycast IPs, AbstractAPI outages: default to the global map
    // rather than assuming a US visitor.
    return 'global'
  }
}

export default function ApprovalMapWidget({ sport: sportProp, sportsbook, apiBase, theme, labels, defaultTab, subAffiliate, world, onEmpty }: Props) {
  // `sport` is the GA4 subject + payload key: the sport slug, or `book:<slug>`
  // in sportsbook mode (the whole widget below reads it as before).
  const sport = sportsbook ? `book:${sportsbook}` : (sportProp ?? '')
  const payloadPath = sportsbook
    ? `/api/v1/sportsbooks/${encodeURIComponent(sportsbook)}/approval-map`
    : `/api/v1/approval-maps/${encodeURIComponent(sportProp ?? '')}`
  const [data, setData] = useState<ApprovalMapData | null>(null)
  const [failed, setFailed] = useState(false)
  const [tab, setTab] = useState<MapTab | null>(world ? (defaultTab ?? null) : 'usa')
  // Which map the arrows return to. Tracks the last map tab shown so leaving
  // the list view lands you back where you were.
  const [lastMapTab, setLastMapTab] = useState<MapOnlyTab>(
    defaultTab && defaultTab !== 'list' ? defaultTab : 'usa',
  )
  const [active, setActive] = useState<{ id: string, x: number, y: number } | null>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const [rgBooks, setRgBooks] = useState<{ name: string, regulatory_full: string }[]>([])
  const [rgOpen, setRgOpen] = useState(false)

  const text = { ...DEFAULT_LABELS, ...(sportsbook ? SPORTSBOOK_LABELS : {}), ...labels }
  // Sportsbook mode: popover / list tiles are LEAGUE logos — rendered on no
  // background (Cole, 2026-10-07).
  const plainTiles = Boolean(sportsbook)

  // Responsible-gambling statements for the footer strip — the same
  // book-level sportsbooks.regulatory_full texts the odds widget shows,
  // served by GET /api/v1/regulatory (see api/regulatory.ts). A failed fetch
  // leaves rgBooks empty and the strip renders without a caret.
  useEffect(() => {
    let cancelled = false
    fetch(`${apiBase}/api/v1/regulatory`)
      .then(async (res) => {
        if (!res.ok) throw new Error(`regulatory fetch failed: ${res.status}`)
        return res.json() as Promise<{ books: { name: string, regulatory_full: string }[] }>
      })
      .then((json) => {
        if (!cancelled) setRgBooks(json.books ?? [])
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [apiBase])

  // Statements for the books THIS map actually shows, in the payload's
  // carrier order (business-priority books first, same as the tiles).
  const rgStatements = useMemo(() => {
    const byName = new Map(rgBooks.map(b => [b.name, b.regulatory_full]))
    return (data?.books ?? [])
      .filter(b => byName.has(b.name))
      .map(b => ({ book: b.name, statement: byName.get(b.name)! }))
  }, [rgBooks, data])

  // Payload and geo are fetched together: geo resolves inside the window the
  // map data is already loading in, so the opening tab costs no extra latency.
  useEffect(() => {
    let cancelled = false

    const load = async () => {
      const [payload, homeTab] = await Promise.all([
        fetch(`${apiBase}${payloadPath}${subAffiliate ? `?aff=${encodeURIComponent(subAffiliate)}` : ''}`).then(async (res) => {
          if (!res.ok) throw new Error(`approval-map fetch failed: ${res.status}`)
          return res.json() as Promise<ApprovalMapData>
        }),
        // No world data -> no Global tab to target, so skip the geo call.
        !world ? Promise.resolve<MapTab>('usa') : (defaultTab ? Promise.resolve(defaultTab) : resolveHomeTab(apiBase)),
      ])
      if (cancelled) return

      const hasCountries = payload.books.some(
        b => (b.countries?.length ?? 0) > 0 || (b.blocked_countries?.length ?? 0) > 0,
      )
      if (payload.betting_states.length === 0 && payload.dfs_states.length === 0 && !hasCountries) {
        trackMapEmpty(sport)
        setFailed(true)
        onEmpty()
        return
      }

      // A US-less sport would open on an empty USA map; fall back to global.
      const usaHasData = payload.betting_states.length > 0 || payload.dfs_states.length > 0
      const opening: MapTab = world && homeTab === 'usa' && !usaHasData ? 'global' : homeTab

      if (payload.unmapped_country_tokens?.length) {
        console.warn(
          `ApprovalMapWidget: ${payload.unmapped_country_tokens.length} region token(s) could not be`
          + ` resolved to a country and are not shown: ${payload.unmapped_country_tokens.join(', ')}`,
        )
      }

      trackMapView(sport, theme, opening)
      setTab(opening)
      if (opening !== 'list') setLastMapTab(opening)
      setData(payload)
    }

    load().catch((e) => {
      if (cancelled) return
      console.warn(`ApprovalMapWidget: no map for "${sport}" —`, (e as Error).message)
      trackMapError(sport)
      setFailed(true)
      onEmpty()
    })

    return () => { cancelled = true }
  }, [sport, payloadPath, apiBase, defaultTab, subAffiliate, world])

  const derived = useMemo(() => {
    if (!data) return null
    const betting = new Set(data.betting_states)
    const dfs = new Set(data.dfs_states)
    const prediction = new Set(data.prediction_states ?? [])
    const index: BookIndex[] = indexBooks(data.books)
    const bet = data.brand.primary ?? FALLBACK.betting
    const colors = {
      bet,
      dfs: data.brand.secondary ?? FALLBACK.dfs,
      // The sport's tertiary colour carries the combination bucket on both map
      // families: betting + DFS on the USA map, betting + prediction on the
      // world maps — in each case the richest category that tab can show.
      combo: data.brand.tertiary ?? FALLBACK.both,
      betpred: data.brand.tertiary ?? FALLBACK.both,
      // Prediction-only coverage. Uses the sport's 4th brand slot ("alternate
      // colour", set on the /affiliate sport page) when one is chosen; falls
      // back to a washed-out tint of the betting colour, which keeps it
      // on-brand automatically and lighter than every other layer, so "a
      // prediction market reaches here" reads as a weaker claim than "a
      // licensed sportsbook operates here" at a glance.
      pred: data.brand.alternate ?? mixHex(bet, '#ffffff', 0.62),
    }
    // Country statuses are resolved once per payload — 175 lookups across up to
    // ~14 carriers is cheap here and keeps hover and tab switching O(1).
    const countryStatus = new Map<string, Status>()
    // Built from the world dataset, which is a superset of Europe's ids.
    //
    // The world maps drop DFS as a colour category on the evidence that no
    // country outside the US and Canada has a DFS operator without also having
    // a sportsbook. If that stops being true the country falls to 'def', so
    // collect those and say so rather than quietly painting them unavailable.
    const dfsOnly: string[] = []
    for (const c of world?.global.countries ?? []) {
      const books = c.id === 'US' ? null : booksInCountry(index, c.id)
      if (books && isDfsOnlyCountry(books)) dfsOnly.push(c.id)
      countryStatus.set(
        c.id,
        // Non-jurisdictions (Antarctica) are never available, whatever a
        // denylist book's absent mention of them would otherwise imply.
        !isJurisdiction(c.region)
          ? 'def'
          // The US answer comes from the state layers, not the carriers'
          // international lists — see booksInUnitedStates() for why.
          : c.id === 'US'
            ? unitedStatesStatus(betting, dfs, prediction)
            : statusOf(books!),
      )
    }
    if (dfsOnly.length) {
      console.warn(
        `ApprovalMapWidget: ${dfsOnly.length} country(s) carry DFS but no sportsbook or`
        + ` prediction market, so the world maps show them as unavailable: ${dfsOnly.join(', ')}.`
        + ' The world legend has no DFS category — revisit if this is now common.',
      )
    }
    return { betting, dfs, prediction, index, colors, countryStatus }
  }, [data, world])

  // Must be stable across renders: MapSurface memoizes its 175-path layer on
  // (shapes, statusFor), so a fresh closure every render would re-render the
  // whole set on every cursor movement.
  const statusFor = useMemo<(id: string) => Status>(() => {
    if (!derived) return () => 'def'
    const { betting: b, dfs: d, prediction: p, countryStatus } = derived
    return tab === 'usa'
      ? (id: string) => stateStatus(id, b, d, p)
      : (id: string) => countryStatus.get(id) ?? 'def'
  }, [derived, tab])

  const activeId = active?.id
  const scope: 'state' | 'country' = tab === 'usa' ? 'state' : 'country'
  useEffect(() => {
    if (!activeId || !tab || tab === 'list') return
    const timer = setTimeout(
      () => trackMapStateEngage(sport, activeId, tab === 'usa' ? 'state' : 'country', statusFor(activeId)),
      500,
    )
    return () => clearTimeout(timer)
  }, [activeId, sport, tab, statusFor])

  const cancelClose = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
  }, [])

  const scheduleClose = useCallback(() => {
    cancelClose()
    closeTimer.current = setTimeout(() => setActive(null), 160)
  }, [cancelClose])

  useEffect(() => () => cancelClose(), [cancelClose])

  // Escape dismisses the popover. Matters most in click mode, where there is no
  // mouse-out to close it, but it is the expected key in both.
  useEffect(() => {
    if (!active) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActive(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [active])

  const goTo = useCallback((next: MapTab, nav: 'tabs' | 'arrow' | 'jump' = 'tabs') => {
    setActive(null)
    setTab(next)
    if (next !== 'list') setLastMapTab(next)
    trackMapTabChange(sport, next, nav)
  }, [sport])

  // Popover anchoring: the shape's bbox centre, converted from user units to
  // rendered pixels and clamped so the panel stays inside the widget.
  const anchor = useCallback((
    point: { x: number, y: number },
    rect: DOMRect,
    vb: [number, number, number, number],
  ) => {
    // The SVG now fills a fixed stage, so its element box is usually a
    // different shape from its viewBox. preserveAspectRatio="xMidYMid meet"
    // scales the content uniformly to fit and centres it, so converting
    // viewBox units to pixels needs that scale AND the letterbox offset —
    // without the offset every popover on the Global and Europe tabs would sit
    // high or left of its country.
    const scale = Math.min(rect.width / vb[2], rect.height / vb[3])
    const offsetX = (rect.width - vb[2] * scale) / 2
    const offsetY = (rect.height - vb[3] * scale) / 2
    const x = (point.x - vb[0]) * scale + offsetX
    const y = (point.y - vb[1]) * scale + offsetY
    return {
      x: Math.min(Math.max(x, 110), Math.max(rect.width - 110, 110)),
      y: Math.max(y, 0),
    }
  }, [])

  if (failed || !data || !derived || !tab) return null

  const { betting, dfs, prediction, index, colors, countryStatus } = derived

  // Which geometry the visible tab draws. EMEA and Global share ids and colours
  // — only the projected extent differs.
  const dataset = world && tab === 'global'
    ? world.global
    : world && tab === 'europe' ? world.europe : null
  const vb = dataset
    ? dataset.viewBox.split(' ').map(Number) as [number, number, number, number]
    : US_VB
  const shapes: MapShape[] = dataset
    ? dataset.countries.map(c => ({ id: c.id, name: c.name, d: c.d, cx: c.cx, cy: c.cy }))
    : US_SHAPES

  const openFor = (id: string, point: Parameters<typeof anchor>[0], rect: DOMRect) => {
    setActive({ id, ...anchor(point, rect, vb) })
  }

  const toggleFor = (id: string, point: Parameters<typeof anchor>[0], rect: DOMRect) => {
    if (active?.id === id) setActive(null)
    else openFor(id, point, rect)
  }

  const activeRegion = active && tab !== 'usa' ? world?.placeById[active.id]?.region : undefined
  const activeBooks: PlaceBooks | null = active
    ? (tab === 'usa'
        ? booksInState(index, active.id, betting)
        : activeRegion && !isJurisdiction(activeRegion)
          ? { sportsbooks: [], dfs: [], prediction: [] }
          : active.id === 'US' ? booksInUnitedStates(index, betting) : booksInCountry(index, active.id))
    : null
  const activeStatus = active ? statusFor(active.id) : null
  const activeName = active
    ? (tab === 'usa'
        ? US_STATES.find(s => s.id === active.id)?.name
        : world?.placeById[active.id]?.name)
    : null
  const activeQualifiers = active && tab !== 'usa' ? countryQualifiers(index, active.id) : []

  // Legend reflects the layers actually present on the visible tab.
  // The legend lists exactly the categories the visible tab actually paints, in
  // the order Cole specified, and nothing else.
  const usStatuses = US_STATES.map(st => stateStatus(st.id, betting, dfs, prediction))
  // The list view carries BOTH taxonomies at once — its USA section is coloured
  // by the state rules and every other section by the country rules — so its key
  // has to cover the union or some of its dots go unexplained.
  const painted = new Set<Status>(
    tab === 'usa'
      ? usStatuses
      : tab === 'list'
        ? [...usStatuses, ...countryStatus.values()]
        : countryStatus.values(),
  )
  // The betting+DFS bucket names prediction only when some of it actually has a
  // prediction market — otherwise a league with no prediction carrier would read
  // "Betting, DFS and Prediction" over states that have no such thing.
  const comboHasPrediction = US_STATES.some(
    st => betting.has(st.id) && dfs.has(st.id) && prediction.has(st.id),
  )

  const ROW_BET = { key: 'bet' as Status, label: text.betting, color: colors.bet }
  const ROW_COMBO = {
    key: 'combo' as Status,
    label: comboHasPrediction ? text.bettingDfsPrediction : text.bettingAndDfs,
    color: colors.combo,
  }
  const ROW_BETPRED = { key: 'betpred' as Status, label: text.bettingAndPrediction, color: colors.betpred }
  const ROW_DFS = { key: 'dfs' as Status, label: text.dfsOnly, color: colors.dfs }
  // Not in Cole's four USA keys, but 2-3 states per Polymarket-carrying sport
  // are prediction-only (F1: Hawaii and Idaho). Painting them grey under
  // "No Availability" would contradict the prediction layer, so the row appears
  // only when such places exist.
  const ROW_PRED = { key: 'pred' as Status, label: text.prediction, color: colors.pred }

  const legend: { label: string, color: string }[] = (tab === 'usa'
    ? [ROW_BET, ROW_COMBO, ROW_DFS, ROW_PRED]
    : tab === 'list'
      ? [ROW_BET, ROW_COMBO, ROW_BETPRED, ROW_DFS, ROW_PRED]
      : [ROW_BET, ROW_BETPRED, ROW_PRED])
    .filter(row => painted.has(row.key))
    .map(({ label, color }) => ({ label, color }))
    .concat([{ label: text.none, color: '#DCDEED' }])

  const TABS: { id: MapTab, label: string }[] = [
    { id: 'usa', label: text.tabUsa },
    { id: 'global', label: text.tabGlobal },
    { id: 'europe', label: text.tabEurope },
    { id: 'list', label: text.tabList },
  ]

  // Left arrow goes to the list, or back to the map you came from when the list
  // is already showing. Right arrow always lands on a map, flipping between USA
  // and Global. The explicit tabs above remain the primary control.
  // Right arrow cycles the maps in MAP_TABS order; left arrow is the list, or
  // the map you came from when the list is already showing.
  const leftTarget: MapTab = tab === 'list' ? lastMapTab : 'list'
  const rightTarget: MapTab = MAP_TABS[(MAP_TABS.indexOf(lastMapTab) + 1) % MAP_TABS.length]!
  const labelFor = (t: MapTab) => TABS.find(x => x.id === t)!.label

  return (
    <div className={`am-root${theme === 'dark' ? ' am-dark' : ''}`}>
      <style>{`
        .am-map path.am-s-bet { fill: ${colors.bet}; }
        .am-map path.am-s-bet:hover, .am-map path.am-on-overlay.am-s-bet { fill: ${hoverColor(colors.bet)}; }
        .am-map path.am-s-dfs { fill: ${colors.dfs}; }
        .am-map path.am-s-dfs:hover, .am-map path.am-on-overlay.am-s-dfs { fill: ${hoverColor(colors.dfs)}; }
        .am-map path.am-s-combo { fill: ${colors.combo}; }
        .am-map path.am-s-combo:hover, .am-map path.am-on-overlay.am-s-combo { fill: ${hoverColor(colors.combo)}; }
        .am-map path.am-s-betpred { fill: ${colors.betpred}; }
        .am-map path.am-s-betpred:hover, .am-map path.am-on-overlay.am-s-betpred { fill: ${hoverColor(colors.betpred)}; }
        .am-map path.am-s-pred { fill: ${colors.pred}; }
        .am-map path.am-s-pred:hover, .am-map path.am-on-overlay.am-s-pred { fill: ${hoverColor(colors.pred)}; }
        .am-list-row.am-row-bet .am-list-dot { background: ${colors.bet}; }
        .am-list-row.am-row-dfs .am-list-dot { background: ${colors.dfs}; }
        .am-list-row.am-row-combo .am-list-dot { background: ${colors.combo}; }
        .am-list-row.am-row-betpred .am-list-dot { background: ${colors.betpred}; }
        .am-list-row.am-row-pred .am-list-dot { background: ${colors.pred}; }
      `}
      </style>

      {world && (
        <div className="am-tabs" role="tablist" aria-label={`${data.title} availability views`}>
          {TABS.map(t => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className={`am-tab${tab === t.id ? ' am-tab-on' : ''}`}
              onClick={() => goTo(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      <div className="am-stage">
        <div className="am-stage-inner">
          {tab === 'list' && world
            ? (
                <ListView
                  index={index}
                  bettingStates={betting}
                  dfsStates={dfs}
                  predictionStates={prediction}
                  usStates={US_STATES}
                  world={world}
                  sport={sport}
                  apiBase={apiBase}
                  plain={plainTiles}
                  labels={{ none: text.none }}
                />
              )
            : (
                <MapSurface
                  key={tab}
                  viewBox={dataset?.viewBox ?? US_MAP_VIEWBOX}
                  shapes={shapes}
                  statusFor={statusFor}
                  activeId={active?.id ?? null}
                  onOpen={openFor}
                  onToggle={toggleFor}
                  onScheduleClose={scheduleClose}
                  onCancelClose={cancelClose}
                  onBackgroundClick={() => setActive(null)}
                  openOn={tab === 'usa' ? 'hover' : 'click'}
                  ariaLabel={`${data.title} availability map — ${tab === 'usa' ? 'United States' : tab === 'europe' ? 'Europe' : 'worldwide'}`}
              variant={tab === 'usa' ? 'us' : 'world'}
            >
              {active && activeBooks && (
                <div
                  className="am-pop"
                  style={{ left: active.x, top: active.y }}
                  onMouseEnter={tab === 'usa' ? cancelClose : undefined}
                  onMouseLeave={tab === 'usa' ? scheduleClose : undefined}
                >
                  <div className="am-pop-state">{activeName ?? active.id}</div>
                  {activeBooks.sportsbooks.length > 0 && (
                    <div className="am-pop-group">
                      <div className="am-pop-label">{text.sportsbooksHeading}</div>
                      <div className="am-pop-tiles">
                        {activeBooks.sportsbooks.map(b => (
                          <BookTile key={b.name} book={b} apiBase={apiBase} sport={sport} place={active.id} scope={scope} plain={plainTiles} />
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Sport approved in this state but no carrying sportsbook is
                      live there yet — say so instead of silently omitting the
                      row. US tab only: countries have no sport-approval layer. */}
                  {tab === 'usa' && activeBooks.sportsbooks.length === 0 && (activeStatus === 'bet' || activeStatus === 'combo') && (
                    <div className="am-pop-none">{text.approvedNoBooks}</div>
                  )}
                  {activeBooks.dfs.length > 0 && (
                    <div className="am-pop-group">
                      <div className="am-pop-label">{text.dfsHeading}</div>
                      <div className="am-pop-tiles">
                        {activeBooks.dfs.map(b => (
                          <BookTile key={b.name} book={b} apiBase={apiBase} sport={sport} place={active.id} scope={scope} plain={plainTiles} />
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Prediction markets are labelled separately so a country
                      reached only by Polymarket is never read as regulated
                      sportsbook coverage. */}
                  {activeBooks.prediction.length > 0 && (
                    <div className="am-pop-group">
                      <div className="am-pop-label">{text.predictionHeading}</div>
                      <div className="am-pop-tiles">
                        {activeBooks.prediction.map(b => (
                          <BookTile key={b.name} book={b} apiBase={apiBase} sport={sport} place={active.id} scope={scope} plain={plainTiles} />
                        ))}
                      </div>
                    </div>
                  )}
                  {activeBooks.sportsbooks.length === 0 && activeBooks.dfs.length === 0 && activeBooks.prediction.length === 0 && activeStatus === 'def' && (
                    <div className="am-pop-none">{text.none}</div>
                  )}
                  {/* The US is state-regulated: a single country-level readout
                      would hide that, so point at the tab that has the detail. */}
                  {tab !== 'usa' && active.id === 'US' && (
                    <button type="button" className="am-pop-jump" onClick={() => goTo('usa', 'jump')}>
                      {betting.size > 0 ? `Approved in ${betting.size} states` : 'Varies by state'} — view state detail →
                    </button>
                  )}
                  {/* Per-book coverage limits, attributed to the book that has
                      them — a province-only carrier must not read as national. */}
                  {activeQualifiers.length > 0 && (
                    <div className="am-pop-quals">
                      {activeQualifiers.map(qf => (
                        <span key={qf.book} className="am-pop-note">{qf.book}: {qf.note}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </MapSurface>
              )}
        </div>
      </div>

      {/* Always rendered; empty where it does not apply. Letting the row appear
          and disappear would put a ~17px shift back into a widget the stage
          above exists to keep still. */}
      <p className="am-hint">
        {tab !== 'usa' && tab !== 'list'
          ? `Click a country for detail${active ? ' · Esc to close' : ''}`
          : ' '}
      </p>

      <div className="am-footer">
        {world && (
          <button
            type="button"
            className="am-arrow"
            onClick={() => goTo(leftTarget, 'arrow')}
            aria-label={`Show ${labelFor(leftTarget)}`}
            title={`Show ${labelFor(leftTarget)}`}
          >
            ‹
          </button>
        )}

        <ul className="am-legend">
          {legend.map(entry => (
            <li key={entry.label}>
              <span className="am-swatch" style={{ background: entry.color }} />
              {entry.label}
            </li>
          ))}
        </ul>

        {world && (
          <button
            type="button"
            className="am-arrow"
            onClick={() => goTo(rightTarget, 'arrow')}
            aria-label={`Show ${labelFor(rightTarget)}`}
            title={`Show ${labelFor(rightTarget)}`}
          >
            ›
          </button>
        )}
      </div>

      {/* Responsible-gambling disclosure — the widget's last row, mirroring the
          odds widget's strip. Caret + scrolling panel only when at least one of
          this map's books has a statement in the catalog. The strip stays
          pinned as the header of the expanded box; statements reveal below. */}
      <div className="am-rg">
        <button
          type="button"
          className="am-rg-toggle"
          disabled={rgStatements.length === 0}
          aria-expanded={rgOpen && rgStatements.length > 0}
          aria-label={rgStatements.length > 0
            ? `${rgOpen ? 'Hide' : 'Show'} responsible gambling statements`
            : undefined}
          onClick={() => {
            if (rgStatements.length === 0) return
            if (!rgOpen) trackMapRGExpand(sport)
            setRgOpen(v => !v)
          }}
        >
          <span>21+ Only. Restrictions and eligibility requirements apply.</span>
          {rgStatements.length > 0 && (
            <svg
              className={`am-rg-caret${rgOpen ? ' am-rg-caret-open' : ''}`}
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          )}
        </button>
        {rgOpen && rgStatements.length > 0 && (
          <div className="am-rg-panel">
            {rgStatements.map(({ book, statement }) => (
              <div key={book}>
                <div className="am-rg-book">{book}</div>
                <p className="am-rg-text">{statement}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
